import { abbreviateNumber } from 'js-abbreviation-number';

export function ParseDecimal<TDefault = number | null | undefined>(
	string: string | null | undefined,
	defaultValue?: TDefault
): number | TDefault;

export function ParseDecimal(
	string: string | null | undefined,
	defaultValue: number | null | undefined = null
): number | null | undefined {
	if (string == null) return defaultValue;

	const number = parseFloat(string.replaceAll(/[,\$%]/g, ''));

	if (isNaN(number)) return defaultValue;

	return number;
}

export type FormatStyles = 'decimal' | 'currency' | 'percent' | 'unit' | 'abbreviation';

export type FormatOptions = Omit<Intl.NumberFormatOptions, 'style'> & { locale?: string };

export const NoDecimalsFormatOptions: Pick<FormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'> = {
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
};

export function FormatNumberAsCurrencyString(
	value: number | undefined,
	precision: number,
	minDigits: number,
	maxDigits: number
): string {
	if (!value) {
		return '';
	}

	let options: FormatOptions = {
		minimumFractionDigits: minDigits,
		maximumFractionDigits: maxDigits,
	};
	return FormatStringAsNumber(FormatFloat(value, precision), 'currency', options) ?? '';
}

export function FormatStringAsNumber(
	value: string | null | undefined,
	style: FormatStyles = 'decimal',
	options?: FormatOptions
): string | null {
	const number = ParseDecimal(value);

	return FormatNumber(number, style, options);
}

export function FormatNumber(
	number: number | null | undefined,
	style: FormatStyles = 'decimal',
	{ locale, currency = 'USD', ...options }: FormatOptions = {}
): string | null {
	if (number == null || isNaN(number)) return null;

	if (style == 'abbreviation') {
		const decimalPlaces = number < 1000000 ? 0 : 2;
		return '$' + abbreviateNumber(number, decimalPlaces, ['', 'K', 'M', 'B', 'T']);
	}

	const formatter = new Intl.NumberFormat(locale, {
		style,
		currency,
		...options,
	});

	// Have to divide by 100 for percents because Intl.NumberFormat expects the number express the result of that division.
	return formatter.format(style == 'percent' ? number / 100 : number);
}

export function FormatFloat(
	value: string | number | null | undefined,
	decimalPlaces: number,
	defaultValue: string = ''
): string {
	const options: Pick<FormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'> = {
		minimumFractionDigits: 1,
		maximumFractionDigits: decimalPlaces,
	};

	const formattedValue =
		typeof value === 'string'
			? FormatStringAsNumber(value, 'decimal', options)
			: FormatNumber(value, 'decimal', options);

	return formattedValue ?? defaultValue;
}

export function FormatDecimal(value: string | number | null | undefined, defaultValue: string = ''): string {
	const formattedValue =
		typeof value === 'string'
			? FormatStringAsNumber(value, 'decimal', NoDecimalsFormatOptions)
			: FormatNumber(value, 'decimal', NoDecimalsFormatOptions);

	return formattedValue ?? defaultValue;
}

export function FormatPercentage(value: string | number | null | undefined, defaultValue: string = ''): string {
	const formattedValue =
		typeof value === 'string' ? FormatStringAsNumber(value, 'percent') : FormatNumber(value, 'percent');

	return formattedValue ?? defaultValue;
}

export function FormatCurrency(value: string | number | null | undefined, defaultValue: string = ''): string {
	const formattedValue =
		typeof value === 'string'
			? FormatStringAsNumber(value, 'currency', NoDecimalsFormatOptions)
			: FormatNumber(value, 'currency', NoDecimalsFormatOptions);

	return formattedValue ?? defaultValue;
}

export function SumAndFormatCurrency(...values: (string | number | null | undefined)[]): string {
	const sum = values.reduce((sum: number, value) => {
		if (typeof value === 'string') return sum + ParseDecimal(value, 0);

		return sum + (value ?? 0);
	}, 0);

	return FormatCurrency(sum);
}

export function ParseAndSum(...values: (string | null | undefined)[]): number {
	return values.reduce((sum, value) => sum + ParseDecimal(value, 0), 0);
}

export function Sum(...values: (string | number | null | undefined)[]): number {
	return values.reduce((sum: number, value) => {
		if (typeof value === 'string') return sum + ParseDecimal(value, 0);

		return sum + (value ?? 0);
	}, 0);
}
